import { useEffect, useMemo, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const ranges = [
  { value: "24h", label: "24h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
  { value: "90d", label: "90d" },
  { value: "1y", label: "1y" },
];

const nf = new Intl.NumberFormat();

function pct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return "0%";
  return `${Number(value).toFixed(1)}%`;
}

function Card({ label, value, hint }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-sm text-slate-500">{label}</div>
      <div className="mt-1 text-3xl font-bold text-slate-900">{value}</div>
      {hint && <div className="mt-1 text-xs text-slate-500">{hint}</div>}
    </div>
  );
}

function Table({ title, rows, columns, empty = "No data yet." }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm overflow-hidden">
      <h3 className="mb-4 text-lg font-semibold text-slate-900">{title}</h3>
      {!rows || rows.length === 0 ? (
        <p className="text-sm text-slate-500">{empty}</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="text-xs uppercase tracking-wide text-slate-500">
              <tr>
                {columns.map((c) => <th key={c.key} className="border-b border-slate-200 pb-2 pr-3">{c.label}</th>)}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx} className="border-b border-slate-100 last:border-b-0">
                  {columns.map((c) => (
                    <td key={c.key} className="py-2 pr-3 align-top text-slate-700">
                      {c.render ? c.render(row) : row[c.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UtmSetupGuide() {
  const example = "https://lionpridefinancial.com/publications?utm_source=facebook&utm_medium=paid_social&utm_campaign=retirement-income&utm_content=video-a";
  return (
    <div className="rounded-2xl border border-cyan-200 bg-cyan-50 p-5 text-sm text-slate-700 shadow-sm">
      <h3 className="text-lg font-semibold text-slate-900">UTM Setup Guide</h3>
      <p className="mt-2">
        Add UTM tags to any ad, email, QR code, or social post link before sending traffic here. Keep names lowercase and consistent so campaigns group cleanly in this dashboard.
      </p>
      <div className="mt-3 grid gap-2 text-xs md:grid-cols-2">
        <div><span className="font-semibold">Meta / Facebook / Instagram:</span> <code>utm_source=facebook</code>, <code>utm_medium=paid_social</code>, <code>utm_campaign=campaign-name</code></div>
        <div><span className="font-semibold">Google Ads:</span> <code>utm_source=google</code>, <code>utm_medium=cpc</code>, <code>utm_campaign=campaign-name</code>, <code>utm_term=keyword</code></div>
        <div><span className="font-semibold">LinkedIn:</span> <code>utm_source=linkedin</code>, <code>utm_medium=paid_social</code>, <code>utm_content=creative-name</code></div>
        <div><span className="font-semibold">Email / GoHighLevel:</span> <code>utm_source=newsletter</code>, <code>utm_medium=email</code>, <code>utm_campaign=sequence-name</code></div>
      </div>
      <div className="mt-3 break-all rounded-xl border border-cyan-100 bg-white p-3 font-mono text-xs text-slate-700">{example}</div>
    </div>
  );
}

export default function TrafficInsightsWidget() {
  const [range, setRange] = useState("30d");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    setLoading(true);
    setError("");
    fetch(`/api/traffic_analytics.php?range=${encodeURIComponent(range)}`, {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(async (res) => {
        const text = await res.text();
        try {
          const json = JSON.parse(text);
          if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`);
          return json;
        } catch (err) {
          throw new Error(`Invalid analytics response: ${text.slice(0, 180)}`);
        }
      })
      .then(setData)
      .catch((err) => setError(err.message || "Unable to load traffic analytics."))
      .finally(() => setLoading(false));
  };

  useEffect(load, [range]);

  const summary = data?.summary || {};
  const series = data?.time_series || [];
  const devices = data?.device_breakdown || [];

  const topFlowRows = useMemo(() => (data?.flows || []).slice(0, 10), [data]);

  return (
    <section className="rounded-3xl bg-slate-50 p-6 shadow-md border border-slate-200">
      <div className="mb-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Visitor Traffic Intelligence</h2>
          <p className="text-sm text-slate-600">Real visitor behavior, page flow, referrers, article performance, and conversion signals.</p>
          {data?.data_source && <p className="mt-1 text-xs text-slate-500">Data source: {data.data_source}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {ranges.map((r) => (
            <button
              key={r.value}
              type="button"
              onClick={() => setRange(r.value)}
              className={`rounded-full px-3 py-1 text-sm font-semibold ${range === r.value ? "bg-slate-900 text-white" : "bg-white text-slate-700 border border-slate-200"}`}
            >
              {r.label}
            </button>
          ))}
          <button type="button" onClick={load} className="rounded-full bg-cyan-600 px-3 py-1 text-sm font-semibold text-white">Refresh</button>
        </div>
      </div>

      {error && <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>}
      {loading && <div className="mb-4 text-sm text-slate-500">Loading traffic analytics…</div>}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card label="Visitors" value={nf.format(summary.visitors || 0)} hint="Unique visitor IDs or fallback IP hashes" />
        <Card label="Sessions" value={nf.format(summary.sessions || 0)} hint="30-minute client sessions when available" />
        <Card label="Page Views" value={nf.format(summary.pageviews || 0)} hint={`${nf.format(summary.bot_or_internal_events || 0)} bot/admin/API/static hits excluded`} />
        <Card label="Conversions" value={nf.format(summary.conversions || 0)} hint={`${pct(summary.conversion_rate)} conversion rate`} />
        <Card label="Avg Pages / Session" value={Number(summary.avg_pages_per_session || 0).toFixed(2)} hint="Public pageviews only" />
        <Card label="Bounce Rate" value={pct(summary.bounce_rate)} hint="Sessions with one public pageview" />
        <Card label="Active Now" value={nf.format(summary.active_last_15m || 0)} hint="Visitors in the last 15 minutes" />
        <Card label="Tracked Events" value={nf.format(summary.total_events || 0)} hint="Raw tracked rows in selected range" />
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm xl:col-span-2">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">Trend</h3>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={series}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="label" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Line type="monotone" dataKey="pageviews" stroke="#0f172a" strokeWidth={2} dot={false} name="Page views" />
              <Line type="monotone" dataKey="visitors" stroke="#0891b2" strokeWidth={2} dot={false} name="Visitors" />
            </LineChart>
          </ResponsiveContainer>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
          <h3 className="mb-4 text-lg font-semibold text-slate-900">Devices</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={devices} dataKey="count" nameKey="label" outerRadius={100} label>
                {devices.map((_, i) => <Cell key={i} fill={["#0f172a", "#0891b2", "#14b8a6", "#64748b", "#94a3b8"][i % 5]} />)}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-6 xl:grid-cols-2">
        <Table
          title="Top Pages"
          rows={data?.top_pages || []}
          columns={[
            { key: "path", label: "Page", render: (r) => <a className="text-cyan-700 hover:underline" href={r.path} target="_blank" rel="noreferrer">{r.path}</a> },
            { key: "views", label: "Views", render: (r) => nf.format(r.views || 0) },
            { key: "visitors", label: "Visitors", render: (r) => nf.format(r.visitors || 0) },
          ]}
        />
        <Table
          title="Top Publications / Articles"
          rows={data?.top_articles || []}
          columns={[
            { key: "title", label: "Article", render: (r) => <a className="text-cyan-700 hover:underline" href={r.path} target="_blank" rel="noreferrer">{r.title || r.path}</a> },
            { key: "views", label: "Views", render: (r) => nf.format(r.views || 0) },
            { key: "visitors", label: "Visitors", render: (r) => nf.format(r.visitors || 0) },
          ]}
        />
        <Table
          title="Landing Pages"
          rows={data?.landing_pages || []}
          columns={[
            { key: "path", label: "First Page", render: (r) => <a className="text-cyan-700 hover:underline" href={r.path} target="_blank" rel="noreferrer">{r.path}</a> },
            { key: "sessions", label: "Sessions", render: (r) => nf.format(r.sessions || 0) },
          ]}
        />
        <Table
          title="Referrers / Sources"
          rows={data?.top_referrers || []}
          columns={[
            { key: "source", label: "Source" },
            { key: "sessions", label: "Sessions", render: (r) => nf.format(r.sessions || 0) },
            { key: "views", label: "Views", render: (r) => nf.format(r.views || 0) },
          ]}
        />
        <div className="space-y-4">
          <Table
            title="UTM Campaigns"
            rows={data?.utm_campaigns || []}
            empty="No UTM-tagged traffic yet."
            columns={[
              { key: "campaign", label: "Campaign" },
              { key: "source_medium", label: "Source / Medium" },
              { key: "sessions", label: "Sessions", render: (r) => nf.format(r.sessions || 0) },
            ]}
          />
          <UtmSetupGuide />
        </div>
        <Table
          title="Common Page Flow"
          rows={topFlowRows}
          empty="Not enough sessionized traffic yet."
          columns={[
            { key: "from", label: "From" },
            { key: "to", label: "To" },
            { key: "count", label: "Count", render: (r) => nf.format(r.count || 0) },
          ]}
        />
      </div>

      <div className="mt-6">
        <Table
          title="Recent Visitor Journeys"
          rows={data?.recent_journeys || []}
          empty="No journeys in this range yet."
          columns={[
            { key: "last_seen", label: "Last Seen" },
            { key: "source", label: "Source" },
            { key: "pages", label: "Path", render: (r) => <span>{(r.pages || []).join(" → ")}</span> },
          ]}
        />
      </div>

      <p className="mt-4 text-xs text-slate-500">
        Notes: admin/API/static asset hits and obvious bots are filtered out of public visitor metrics. Older traffic logged before this patch may not include visitor/session IDs, device type, or UTM data, so those fields improve going forward.
      </p>
    </section>
  );
}
